import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Loader2, Camera, Upload, User, Save, HeartPulse, ClipboardEdit,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ID_TYPES = ["Aadhaar", "PAN", "Voter ID", "Driving License", "Passport", "Other"];
const MARITAL = ["Single", "Married", "Widow", "Widower", "Divorced"];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-", "Unknown"];
const CATEGORIES = ["SC", "ST", "OBC", "General"];

// All editable fields surfaced in this drawer — mirrors WorkmenDrawer's Personal /
// Physical / Medical grouping so Payroll Manpower data parity is 1:1 with the
// Contractor Workmen roster. Auto-filled fields (name, mobile, NCC site, ID proof,
// DOB, age, city/state, trade) are read-only from the contractor profile.
const EMPTY_FORM = {
  ncc_site_name: "",
  source: "",
  source_agency_name: "",
  // Personal
  father_husband_name: "",
  gender: "",
  blood_group: "",
  nationality: "Indian",
  religion: "",
  category: "",
  marital_status: "",
  children_count: "",
  mother_tongue: "",
  other_languages: "",
  address: "",
  emergency_contact_name: "",
  emergency_contact_number: "",
  // Physical Condition
  vision: "",
  hearing: "",
  height: "",
  weight: "",
  // Medical Condition
  physical_deformities: "",
  medical_history: "",
  // Form-XI Physical Examination
  phys_exam_blood_pressure: "",
  phys_exam_pulse: "",
  phys_exam_refractive_error: "",
  phys_exam_colour_vision: "",
  phys_exam_disability: "",
  phys_exam_arm_function: "",
  phys_exam_leg_function: "",
  // Form-XI Enquiry of Previous History
  hist_varicose: "",
  hist_seizure: "",
  hist_vertigo: "",
  hist_acrophobia: "",
  hist_diabetes: "",
  hist_stroke: "",
  hist_heart_diseases: "",
  hist_major_illness_surgery: "",
  hist_symptoms_visible: "",
  hist_others: "",
  // Photo override (data URL) — falls back to contractor.photo when blank
  photo_base64: "",
};

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

async function compressImage(file, maxBytes = 5 * 1024 * 1024) {
  if (file.size <= maxBytes) return await fileToBase64(file);
  const dataUrl = await fileToBase64(file);
  const img = await new Promise((res, rej) => {
    const im = new Image();
    im.onload = () => res(im);
    im.onerror = rej;
    im.src = dataUrl;
  });
  let scale = 1;
  let quality = 0.92;
  let out = dataUrl;
  const byteSize = (b64) => Math.floor((b64.length - "data:image/jpeg;base64,".length) * 0.75);
  for (let i = 0; i < 8; i++) {
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    out = canvas.toDataURL("image/jpeg", quality);
    if (byteSize(out) <= maxBytes) return out;
    if (quality > 0.55) quality -= 0.1; else scale *= 0.85;
  }
  return out;
}

const AUTO_FIELDS = [
  { label: "Name", key: "name" },
  { label: "Mobile", key: "contact_phone" },
  { label: "Designation / Trade", key: "_trade" },
  { label: "ID Proof", key: "_idproof" },
  { label: "Date of Birth", key: "date_of_birth" },
  { label: "Age", key: "_age" },
  { label: "City / State", key: "_loc" },
];

function autoVal(c, k) {
  if (k === "_trade") return (c.trades && c.trades[0]) || "—";
  if (k === "_idproof") return c.id_proof_type && c.id_proof_number ? `${c.id_proof_type} ${c.id_proof_number}` : "—";
  if (k === "_age") return c.age_years ? `${c.age_years} yrs` : "—";
  if (k === "_loc") return [c.city, c.state].filter(Boolean).join(", ") || "—";
  return c[k] || "—";
}

/**
 * Medical / Induction Form Entry drawer for Payroll Manpower.
 * Identical fields & grouping to the Workmen Roster (WorkmenDrawer.jsx)
 * so that the Print Screening Form / Identity Card / Form-XI Medical
 * Certificate pull from the same data set.
 */
export default function PayrollScreenFormDrawer({ open, contractor, authToken, onClose, onSaved, adminMode = false }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [nccSites, setNccSites] = useState([]);  // NCC Sites — mapped from Masters

  useEffect(() => {
    if (!open || !contractor) return;
    const init = { ...EMPTY_FORM };
    Object.keys(EMPTY_FORM).forEach((k) => {
      const v = contractor[k];
      if (v !== undefined && v !== null) init[k] = v;
    });
    setForm(init);
    // Pull active NCC Sites from Masters so admin can map this Payroll Manpower's site.
    (async () => {
      try {
        const m = await axios.get(`${API}/meta/trades`);
        setNccSites(m.data?.ncc_sites || []);
      } catch { /* ignore */ }
    })();
  }, [open, contractor]);

  // Auto-derive "Any Physical Deformities" textarea from Form-XI Physical Examination sub-fields.
  useEffect(() => {
    const parts = [
      ["BP", form.phys_exam_blood_pressure],
      ["Pulse", form.phys_exam_pulse],
      ["Refractive Error", form.phys_exam_refractive_error],
      ["Colour Vision", form.phys_exam_colour_vision],
      ["Disability", form.phys_exam_disability],
      ["Arm Function & Grip", form.phys_exam_arm_function],
      ["Leg & Foot Function", form.phys_exam_leg_function],
    ].filter(([, v]) => (v || "").toString().trim() !== "")
     .map(([k, v]) => `${k}: ${String(v).trim()}`);
    const summary = parts.join("; ");
    setForm((f) => f.physical_deformities === summary ? f : { ...f, physical_deformities: summary });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.phys_exam_blood_pressure, form.phys_exam_pulse, form.phys_exam_refractive_error,
    form.phys_exam_colour_vision, form.phys_exam_disability, form.phys_exam_arm_function,
    form.phys_exam_leg_function,
  ]);

  // Auto-derive "Any Medical History" textarea from Form-XI Enquiry of Previous History sub-fields.
  useEffect(() => {
    const parts = [
      ["Varicose", form.hist_varicose],
      ["Seizure", form.hist_seizure],
      ["Vertigo", form.hist_vertigo],
      ["Acrophobia", form.hist_acrophobia],
      ["Diabetes", form.hist_diabetes],
      ["Stroke", form.hist_stroke],
      ["Heart Diseases", form.hist_heart_diseases],
      ["Major Illness/Surgery", form.hist_major_illness_surgery],
      ["Symptoms Visible", form.hist_symptoms_visible],
      ["Others", form.hist_others],
    ].filter(([, v]) => (v || "").toString().trim() !== "")
     .map(([k, v]) => `${k}: ${String(v).trim()}`);
    const summary = parts.join("; ");
    setForm((f) => f.medical_history === summary ? f : { ...f, medical_history: summary });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.hist_varicose, form.hist_seizure, form.hist_vertigo, form.hist_acrophobia,
    form.hist_diabetes, form.hist_stroke, form.hist_heart_diseases,
    form.hist_major_illness_surgery, form.hist_symptoms_visible, form.hist_others,
  ]);

  async function onPhotoFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const b64 = await compressImage(f, 5 * 1024 * 1024);
      setForm((prev) => ({ ...prev, photo_base64: b64 }));
    } catch {
      toast.error("Could not read or compress photo");
    }
  }

  async function save() {
    if ((form.source || "").toUpperCase() === "AGENCY" && !(form.source_agency_name || "").trim()) {
      toast.error("Agency Name is required when Source = AGENCY");
      return;
    }
    setBusy(true);
    try {
      // Partial update — merge over the existing contractor body so server-side full-PUT accepts.
      const payload = { ...contractor };
      Object.keys(EMPTY_FORM).forEach((k) => {
        const v = form[k];
        if (k === "children_count") {
          payload[k] = v === "" || v === null ? null : Number(v);
        } else {
          payload[k] = (v ?? "").toString().trim() || null;
        }
      });
      const r = await axios.put(
        adminMode ? `${API}/contractors/${contractor.id}` : `${API}/contractor-auth/me`,
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      toast.success(adminMode ? "Submission updated" : "Medical / Induction form updated");
      const next = r.data?.contractor || r.data || payload;
      onSaved?.(next);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  }

  // Resolve photo preview — prefer freshly captured base64, else contractor photo url, else contractor.photo_base64
  const photoPreview = useMemo(() => {
    if (form.photo_base64) return form.photo_base64;
    if (contractor?.photo_base64) return contractor.photo_base64;
    if (contractor?.photo?.url) return contractor.photo.url;
    return "";
  }, [form.photo_base64, contractor]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/40" onClick={onClose}>
      <aside
        className="flex h-full w-full max-w-3xl flex-col border-l-4 border-ncc-gold bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="payroll-screen-form-drawer"
      >
        <header className="flex items-start justify-between border-b-2 border-ncc-navy bg-ncc-navy px-5 py-4 text-white">
          <div>
            <div className="industrial-label text-ncc-gold2">// Medical / Induction Form Entry</div>
            <h2 className="mt-1 font-heading text-xl font-extrabold tracking-tight">
              {contractor?.name || "Payroll Manpower"}
            </h2>
            <div className="mt-1 font-mono text-xs text-white/80">
              <span className="rounded bg-white/10 px-2 py-0.5 inline-flex items-center gap-1">
                <ClipboardEdit className="h-3 w-3" /> Mirrors Workmen Roster · Form-XI compliant
              </span>
            </div>
          </div>
          <button onClick={onClose} data-testid="payroll-screen-form-close" className="rounded-md p-1 hover:bg-white/10">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto bg-slate-50 p-4">
          {/* Auto-filled from your profile */}
          <div className="rounded-md border-2 border-emerald-300 bg-emerald-50 p-3" data-testid="payroll-sf-autofilled">
            <div className="industrial-label text-[10px] text-emerald-700">// Auto-filled from your profile</div>
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
              {AUTO_FIELDS.map((a) => (
                <div key={a.key} className="font-mono text-[11px] text-slate-700">
                  <span className="industrial-label text-slate-500">{a.label}:</span>{" "}
                  <b className="text-ncc-navy">{autoVal(contractor || {}, a.key)}</b>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            {/* GROUP 1 BANNER · Personal Information */}
            <div className="col-span-2 rounded-md border-l-4 border-ncc-navy bg-slate-100 px-3 py-2" data-testid="payroll-sf-group-personal">
              <div className="industrial-label text-[10px] text-ncc-navy">// Personal Information</div>
            </div>

            {/* NCC SITE — mapped from Masters */}
            <Field label="NCC Site" full hint={nccSites.length === 0 ? "No active sites in Masters yet — ask an admin to add some." : "Mapped from Masters (active sites only)"}>
              <select
                value={form.ncc_site_name}
                onChange={(e) => setForm({ ...form, ncc_site_name: e.target.value })}
                data-testid="payroll-sf-ncc_site_name"
                className={inputCls}
              >
                <option value="">— Select NCC Site —</option>
                {nccSites.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
                {/* Preserve a custom legacy value if not in master list */}
                {form.ncc_site_name && !nccSites.includes(form.ncc_site_name) && (
                  <option value={form.ncc_site_name}>{form.ncc_site_name} (legacy)</option>
                )}
              </select>
            </Field>

            {/* SOURCE */}
            <Field label="Source (replaces Sub-Contractor on ID Card)">
              <select
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                data-testid="payroll-sf-source"
                className={inputCls}
              >
                <option value="">— Select —</option>
                <option value="NCCL">NCCL</option>
                <option value="AGENCY">AGENCY</option>
              </select>
            </Field>
            <Field label="Agency Name" hint={form.source === "AGENCY" ? "Required when Source = AGENCY" : "Used only when Source = AGENCY"}>
              <input
                value={form.source_agency_name}
                disabled={form.source !== "AGENCY"}
                onChange={(e) => setForm({ ...form, source_agency_name: e.target.value })}
                placeholder="Agency name"
                data-testid="payroll-sf-source_agency_name"
                className={`${inputCls} ${form.source !== "AGENCY" ? "cursor-not-allowed bg-slate-100" : ""}`}
              />
            </Field>

            {/* PHOTO */}
            <Field label="Photo" full>
              <div className="flex items-center gap-3">
                <div className="grid h-16 w-16 place-items-center overflow-hidden rounded-full border-2 border-ncc-gold/40 bg-white">
                  {photoPreview ? (
                    <img src={photoPreview} alt="" className="h-full w-full object-cover" />
                  ) : <User className="h-7 w-7 text-slate-400" />}
                </div>
                <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border-2 border-ncc-navy bg-white px-2.5 py-1.5 industrial-label text-ncc-navy hover:bg-slate-50">
                  <Camera className="h-3.5 w-3.5" />
                  Camera
                  <input type="file" accept="image/*" capture="environment" onChange={onPhotoFile} className="hidden" data-testid="payroll-sf-photo-camera" />
                </label>
                <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border-2 border-ncc-navy bg-white px-2.5 py-1.5 industrial-label text-ncc-navy hover:bg-slate-50">
                  <Upload className="h-3.5 w-3.5" />
                  Upload
                  <input type="file" accept="image/*" onChange={onPhotoFile} className="hidden" data-testid="payroll-sf-photo-upload" />
                </label>
                {form.photo_base64 && (
                  <button type="button" onClick={() => setForm((f) => ({ ...f, photo_base64: "" }))} className="text-xs text-red-600 underline">Remove</button>
                )}
              </div>
            </Field>

            <Field label="Father's / Husband's Name" full>
              <input value={form.father_husband_name} onChange={(e) => setForm({ ...form, father_husband_name: e.target.value })} placeholder="e.g. Ramesh Kumar" data-testid="payroll-sf-father_husband_name" className={inputCls} />
            </Field>

            <Field label="Gender">
              <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} data-testid="payroll-sf-gender" className={inputCls}>
                <option value="">—</option>
                <option>Male</option><option>Female</option><option>Other</option>
              </select>
            </Field>
            <Field label="Blood Group">
              <select value={form.blood_group} onChange={(e) => setForm({ ...form, blood_group: e.target.value })} data-testid="payroll-sf-blood_group" className={inputCls}>
                <option value="">—</option>
                {BLOOD_GROUPS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </Field>

            <Field label="Nationality">
              <input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} placeholder="Indian" data-testid="payroll-sf-nationality" className={inputCls} />
            </Field>
            <Field label="Religion (Optional)">
              <input value={form.religion} onChange={(e) => setForm({ ...form, religion: e.target.value })} placeholder="e.g. Hindu / Muslim / Christian" data-testid="payroll-sf-religion" className={inputCls} />
            </Field>

            <Field label="Category (Optional)">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="payroll-sf-category" className={inputCls}>
                <option value="">—</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Marital Status">
              <select value={form.marital_status} onChange={(e) => setForm({ ...form, marital_status: e.target.value })} data-testid="payroll-sf-marital_status" className={inputCls}>
                <option value="">—</option>
                {MARITAL.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>

            <Field label="No. of Children">
              <input type="number" min="0" max="20" value={form.children_count} onChange={(e) => setForm({ ...form, children_count: e.target.value })} data-testid="payroll-sf-children_count" className={inputCls} />
            </Field>
            <Field label="Mother Tongue">
              <input value={form.mother_tongue} onChange={(e) => setForm({ ...form, mother_tongue: e.target.value })} placeholder="e.g. Hindi" data-testid="payroll-sf-mother_tongue" className={inputCls} />
            </Field>

            <Field label="Other Languages Known" full>
              <input value={form.other_languages} onChange={(e) => setForm({ ...form, other_languages: e.target.value })} placeholder="e.g. English, Tamil" data-testid="payroll-sf-other_languages" className={inputCls} />
            </Field>

            <Field label="Address" full>
              <textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="House / Street / Landmark" data-testid="payroll-sf-address" className={`${inputCls} font-sans`} />
            </Field>

            <Field label="Emergency Contact Name">
              <input value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} placeholder="e.g. Wife / Father" data-testid="payroll-sf-emergency_contact_name" className={inputCls} />
            </Field>
            <Field label="Emergency Contact Number">
              <input value={form.emergency_contact_number} onChange={(e) => setForm({ ...form, emergency_contact_number: e.target.value.replace(/[^\d+\s-]/g, "").slice(0, 15) })} placeholder="10 digits" data-testid="payroll-sf-emergency_contact_number" className={inputCls} />
            </Field>

            {/* GROUP 2 BANNER · Physical Condition */}
            <div className="col-span-2 mt-1 flex items-center gap-2 rounded-md border-l-4 border-emerald-500 bg-emerald-50 px-3 py-2" data-testid="payroll-sf-group-physical">
              <HeartPulse className="h-3.5 w-3.5 text-emerald-700" />
              <div className="industrial-label text-[10px] text-emerald-900">// Physical Condition</div>
            </div>
            <Field label="Vision">
              <input value={form.vision} onChange={(e) => setForm({ ...form, vision: e.target.value })} placeholder="e.g. 6/6, Specs" data-testid="payroll-sf-vision" className={inputCls} />
            </Field>
            <Field label="Hearing">
              <input value={form.hearing} onChange={(e) => setForm({ ...form, hearing: e.target.value })} placeholder="Normal / Impaired" data-testid="payroll-sf-hearing" className={inputCls} />
            </Field>
            <Field label="Height">
              <input value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} placeholder="e.g. 5'8&quot; / 173 cm" data-testid="payroll-sf-height" className={inputCls} />
            </Field>
            <Field label="Weight">
              <input value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} placeholder="e.g. 70 kg" data-testid="payroll-sf-weight" className={inputCls} />
            </Field>

            {/* GROUP 3 BANNER · Medical Condition */}
            <div className="col-span-2 mt-1 flex items-center gap-2 rounded-md border-l-4 border-emerald-600 bg-emerald-50 px-3 py-2" data-testid="payroll-sf-group-medical">
              <HeartPulse className="h-3.5 w-3.5 text-emerald-700" />
              <div className="industrial-label text-[10px] text-emerald-900">// Medical Condition</div>
            </div>
            <Field label="Any Physical Deformities" full>
              <textarea
                rows={2}
                readOnly
                value={form.physical_deformities}
                placeholder="Auto-filled from Physical Examination below…"
                data-testid="payroll-sf-physical_deformities"
                className={`${inputCls} cursor-not-allowed bg-slate-100 font-sans`}
              />
              <div className="mt-0.5 font-mono text-[10px] text-slate-500">Auto-summary of Physical Examination fields below.</div>
            </Field>
            <Field label="Any Medical History" full>
              <textarea
                rows={2}
                readOnly
                value={form.medical_history}
                placeholder="Auto-filled from Enquiry of Previous History below…"
                data-testid="payroll-sf-medical_history"
                className={`${inputCls} cursor-not-allowed bg-slate-100 font-sans`}
              />
              <div className="mt-0.5 font-mono text-[10px] text-slate-500">Auto-summary of Enquiry of Previous History fields below.</div>
            </Field>

            {/* PHYSICAL EXAMINATION (Form XI) */}
            <div className="col-span-2 mt-1 rounded-md border border-emerald-200 bg-emerald-50/40 px-3 py-1.5" data-testid="payroll-sf-subgroup-phys-exam">
              <div className="industrial-label text-[10px] text-emerald-800">// Physical Examination · Form-XI</div>
            </div>
            <Field label="Blood Pressure">
              <input value={form.phys_exam_blood_pressure} onChange={(e) => setForm({ ...form, phys_exam_blood_pressure: e.target.value })} placeholder="e.g. 120/80 mmHg" data-testid="payroll-sf-phys_exam_blood_pressure" className={inputCls} />
            </Field>
            <Field label="Pulse">
              <input value={form.phys_exam_pulse} onChange={(e) => setForm({ ...form, phys_exam_pulse: e.target.value })} placeholder="e.g. 72 bpm" data-testid="payroll-sf-phys_exam_pulse" className={inputCls} />
            </Field>
            <Field label="Refractive Error">
              <input value={form.phys_exam_refractive_error} onChange={(e) => setForm({ ...form, phys_exam_refractive_error: e.target.value })} placeholder="None / describe" data-testid="payroll-sf-phys_exam_refractive_error" className={inputCls} />
            </Field>
            <Field label="Colour Vision">
              <input value={form.phys_exam_colour_vision} onChange={(e) => setForm({ ...form, phys_exam_colour_vision: e.target.value })} placeholder="Normal / Defective" data-testid="payroll-sf-phys_exam_colour_vision" className={inputCls} />
            </Field>
            <Field label="Any Disability">
              <input value={form.phys_exam_disability} onChange={(e) => setForm({ ...form, phys_exam_disability: e.target.value })} placeholder="None / describe" data-testid="payroll-sf-phys_exam_disability" className={inputCls} />
            </Field>
            <Field label="Arm Function & Grip">
              <input value={form.phys_exam_arm_function} onChange={(e) => setForm({ ...form, phys_exam_arm_function: e.target.value })} placeholder="Normal / Weak / describe" data-testid="payroll-sf-phys_exam_arm_function" className={inputCls} />
            </Field>
            <Field label="Leg & Foot Function" full>
              <input value={form.phys_exam_leg_function} onChange={(e) => setForm({ ...form, phys_exam_leg_function: e.target.value })} placeholder="Normal / Limp / describe" data-testid="payroll-sf-phys_exam_leg_function" className={inputCls} />
            </Field>

            {/* ENQUIRY OF PREVIOUS HISTORY (Form XI) */}
            <div className="col-span-2 mt-1 rounded-md border border-emerald-200 bg-emerald-50/40 px-3 py-1.5" data-testid="payroll-sf-subgroup-hist">
              <div className="industrial-label text-[10px] text-emerald-800">// Enquiry of Previous History · Form-XI</div>
            </div>
            <Field label="Varicose">
              <input value={form.hist_varicose} onChange={(e) => setForm({ ...form, hist_varicose: e.target.value })} placeholder="No / Yes" data-testid="payroll-sf-hist_varicose" className={inputCls} />
            </Field>
            <Field label="Seizure">
              <input value={form.hist_seizure} onChange={(e) => setForm({ ...form, hist_seizure: e.target.value })} placeholder="No / Yes" data-testid="payroll-sf-hist_seizure" className={inputCls} />
            </Field>
            <Field label="Vertigo">
              <input value={form.hist_vertigo} onChange={(e) => setForm({ ...form, hist_vertigo: e.target.value })} placeholder="No / Yes" data-testid="payroll-sf-hist_vertigo" className={inputCls} />
            </Field>
            <Field label="Acrophobia">
              <input value={form.hist_acrophobia} onChange={(e) => setForm({ ...form, hist_acrophobia: e.target.value })} placeholder="No / Yes" data-testid="payroll-sf-hist_acrophobia" className={inputCls} />
            </Field>
            <Field label="Diabetes">
              <input value={form.hist_diabetes} onChange={(e) => setForm({ ...form, hist_diabetes: e.target.value })} placeholder="No / Yes" data-testid="payroll-sf-hist_diabetes" className={inputCls} />
            </Field>
            <Field label="Stroke">
              <input value={form.hist_stroke} onChange={(e) => setForm({ ...form, hist_stroke: e.target.value })} placeholder="No / Yes" data-testid="payroll-sf-hist_stroke" className={inputCls} />
            </Field>
            <Field label="Heart Diseases" full>
              <input value={form.hist_heart_diseases} onChange={(e) => setForm({ ...form, hist_heart_diseases: e.target.value })} placeholder="No / Yes / describe" data-testid="payroll-sf-hist_heart_diseases" className={inputCls} />
            </Field>
            <Field label="Major Illness or Surgery" full>
              <input value={form.hist_major_illness_surgery} onChange={(e) => setForm({ ...form, hist_major_illness_surgery: e.target.value })} placeholder="None / describe" data-testid="payroll-sf-hist_major_illness_surgery" className={inputCls} />
            </Field>
            <Field label="Symptoms Visible" full>
              <input value={form.hist_symptoms_visible} onChange={(e) => setForm({ ...form, hist_symptoms_visible: e.target.value })} placeholder="None / describe" data-testid="payroll-sf-hist_symptoms_visible" className={inputCls} />
            </Field>
            <Field label="Others, if any" full>
              <input value={form.hist_others} onChange={(e) => setForm({ ...form, hist_others: e.target.value })} placeholder="Describe…" data-testid="payroll-sf-hist_others" className={inputCls} />
            </Field>
          </div>

          <div className="mt-4 font-mono text-[11px] text-slate-500">
            Saved fields are used automatically when you press <b>Print Form-XI</b>,
            {" "}<b>Print Screening Form</b> or <b>Print Identity Card</b>.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t-2 border-ncc-navy bg-slate-50 px-5 py-3">
          <button type="button" onClick={onClose} className="border-2 border-ncc-navy bg-white px-4 py-2 industrial-label">Cancel</button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            data-testid="payroll-screen-form-save"
            className="inline-flex items-center gap-2 border-2 border-ncc-navy bg-ncc-gold px-4 py-2 industrial-label disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {busy ? "Saving…" : "Save & Update Printables"}
          </button>
        </div>
      </aside>
    </div>
  );
}

const inputCls = "w-full rounded-md border-2 border-slate-300 bg-white px-2.5 py-1.5 font-mono text-xs focus:border-ncc-navy focus:outline-none";

function Field({ label, hint, children, full }) {
  return (
    <label className={`block ${full ? "col-span-2" : ""}`}>
      <div className="industrial-label mb-1 text-[10px] text-slate-500">{label}</div>
      {children}
      {hint && <div className="mt-0.5 font-mono text-[10px] text-slate-500">{hint}</div>}
    </label>
  );
}
